/* eslint-disable no-underscore-dangle */
import { useMemo, useState } from 'react';

import { Button, Link, Skeleton, Stack, Typography } from '@mui/material';
import { GridColDef, GridRow } from '@mui/x-data-grid-pro';

import isEmpty from 'lodash/isEmpty';

import DataGrid from '@/components/DataGrid';
import CpuIcon from '@/components/Icons/CpuIcon';
import LinkIcon from '@/components/Icons/LinkIcon';
import XCircleIcon from '@/components/Icons/XCircleIcon';
import useIsMultipleTeamsEnabled from '@/hooks/useIsMultipleTeamsEnabled';
import { links } from '@/localization';
import { BasetenPageEnum } from '@/routes/types';
import { WorkspaceChainFragment } from '@/sections/Overview/Chains/__generated__/fragments.generated';
import { useChains, usePinnedAPI as useChainsPinnedAPI } from '@/sections/Overview/Chains/hooks';
import { WorkspaceModelFragment } from '@/sections/Overview/Models/__generated__/fragments.generated';
import { useModels, usePinnedAPI as useModelsPinnedAPI } from '@/sections/Overview/Models/hooks';
import TeamName from '@/sections/Teams/TeamName';
import ListOptions from '@/sections/Workspace/Filter/ListOptions';
import Search from '@/sections/Workspace/Filter/Search';
import VersionsCountByStatus from '@/sections/Workspace/VersionsCountByStatus';
import { getVersionsByStatus, sortVersionsByStatus } from '@/sections/Workspace/utils';
import { dateDisplay } from '@/utils/date';
import Fuse from 'fuse.js';
import moment from 'moment';

import ChainActions from '../../Chains/Actions';
import ModelActions from '../../Models/Actions';
import { BASE_FUSE_SEARCH_OPTIONS, mapFilterToTitle, mapSortByToTitle } from '../../constants';
import { useFilterQuery } from '../../hooks';
import ProductionStatus from '../../sections/ProductionStatus';
import { IconContainer, dataGridProps, dataGridStyles } from '../../styled';
import { Filter, SortBy, WorkspaceSections } from '../../types';
import { filterChainsByStatus, filterModelsByStatus } from '../../utils';
import { AppLink, DedicatedEmptyState, ModelsAnimationContainer, Section } from '../styled';
import { getEntitiesWithDeployments, sortEntities } from './utils';

enum TypeFilter {
  All = 'all',
  Models = 'models',
  Chains = 'chains',
}

const mapTypeFilterToTitle = {
  [TypeFilter.All]: 'All',
  [TypeFilter.Models]: 'Models',
  [TypeFilter.Chains]: 'Chains',
};

const MAX_ITEMS_TO_SHOW = 9;
const ROW_HEIGHT = 67;

const FUSE_SEARCH_OPTIONS = {
  ...BASE_FUSE_SEARCH_OPTIONS,
  keys: ['name', 'id'],
};

function DedicatedDeployments() {
  const { models, loading: modelsLoading } = useModels(true);
  const { chains, loading: chainsLoading } = useChains(true);
  const { filterQuery, setFilterQuery } = useFilterQuery(WorkspaceSections.Overview);
  const [selectedFilter, setSelectedFilter] = useState<Filter>(Filter.All);
  const [selectedSortBy, setSelectedSortBy] = useState<SortBy>(SortBy.PRODUCTION);
  const [selectedTypeFilter, setSelectedTypeFilter] = useState<TypeFilter>(TypeFilter.All);
  const isMultipleTeamsEnabled = useIsMultipleTeamsEnabled();

  const isLoading = modelsLoading || chainsLoading;
  const unfilteredEntities = useMemo(() => [...models, ...chains], [models, chains]);

  const modelsPinnedAPI = useModelsPinnedAPI();
  const chainsPinnedAPI = useChainsPinnedAPI();

  const filteredModelsByStatus = useMemo(() => {
    return filterModelsByStatus(models, selectedFilter);
  }, [models, selectedFilter]);

  const filteredChainsByStatus = useMemo(() => {
    return filterChainsByStatus(chains, selectedFilter);
  }, [chains, selectedFilter]);

  const entities = useMemo(() => {
    if (selectedTypeFilter === TypeFilter.Models) {
      return filteredModelsByStatus;
    }

    if (selectedTypeFilter === TypeFilter.Chains) {
      return filteredChainsByStatus;
    }

    return [...filteredModelsByStatus, ...filteredChainsByStatus];
  }, [filteredModelsByStatus, filteredChainsByStatus, selectedTypeFilter]);

  const fuse = useMemo(() => {
    return new Fuse(entities, FUSE_SEARCH_OPTIONS);
  }, [entities]);

  const sortedEntities = useMemo(() => {
    const entitiesWithDeployments = getEntitiesWithDeployments(entities);
    return sortEntities(entitiesWithDeployments, selectedSortBy);
  }, [entities, selectedSortBy]);

  const filteredEntities = useMemo(() => {
    if (!filterQuery) {
      return sortedEntities;
    }

    const results = fuse.search(filterQuery);
    return results.map((result) => result.item);
  }, [fuse, filterQuery, sortedEntities]);

  const entitiesToShow = filteredEntities.slice(0, MAX_ITEMS_TO_SHOW);
  const noSearchResults = filteredEntities.length === 0 && unfilteredEntities.length > 0;

  function clearSearch() {
    setFilterQuery('');
    setSelectedFilter(Filter.All);
    setSelectedSortBy(SortBy.RECENTLY_ACTIVE);
    setSelectedTypeFilter(TypeFilter.All);
  }

  function getContent() {
    if (isLoading) {
      return (
        <Stack pt={1}>
          <Skeleton variant="text" width="30%" />
          <Skeleton variant="text" width="70%" />
          <Skeleton variant="text" width="50%" />
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="text" width="40%" />
          <Skeleton variant="text" width="50%" />
          <Skeleton variant="text" width="40%" />
          <Skeleton variant="text" width="30%" />
          <Skeleton variant="text" width="40%" />
        </Stack>
      );
    }

    if (isEmpty(entitiesToShow)) {
      if (noSearchResults) {
        return (
          <DedicatedEmptyState>
            <Stack gap={3} textAlign="center" alignItems="center">
              <Stack gap={1}>
                <Typography variant="h4">No results found</Typography>
                <Typography variant="body1" color="text.muted">
                  Try adjusting your search or filters.
                </Typography>
              </Stack>

              <Button variant="outlined" endIcon={<XCircleIcon />} onClick={clearSearch}>
                Clear search
              </Button>
            </Stack>
          </DedicatedEmptyState>
        );
      }

      return (
        <DedicatedEmptyState>
          <ModelsAnimationContainer />
          <Stack gap={4} maxWidth={600} textAlign="center" alignItems="center">
            <Stack gap={2}>
              <Typography variant="h4">Create your first dedicated deployment</Typography>
              <Typography variant="body1" color="text.muted">
                Quickly deploy and scale production-grade models with full control. Start with a
                model from{' '}
                <Link href={links.basetenModelLibrary} target="_blank">
                  our library
                </Link>{' '}
                or{' '}
                <Link href={links.basetenDocsDevelopmentConcepts} target="_blank">
                  deploy your own
                </Link>{' '}
                in just a few commands.
              </Typography>
            </Stack>
            <Stack direction="row" gap={2}>
              <Button
                variant="outlined"
                href={links.basetenDocsDeployingYourFirstModel}
                target="_blank"
              >
                Deploy a model
              </Button>
              <Button variant="outlined" href={links.basetenDocsChainsOverview} target="_blank">
                Deploy a Chain
              </Button>
            </Stack>
          </Stack>
        </DedicatedEmptyState>
      );
    }

    const columns: GridColDef<WorkspaceModelFragment | WorkspaceChainFragment>[] = [
      {
        field: 'name',
        headerName: 'Name',
        sortable: false,
        disableColumnMenu: true,
        flex: 1,
        renderCell: ({ row }) => {
          const Icon = row.__typename === 'ModelType' ? CpuIcon : LinkIcon;
          const displayTime = dateDisplay(moment(row.lastActivity));

          return (
            <Stack width="100%" gap={1.5} direction="row" alignItems="center">
              <IconContainer>
                <Icon color="secondary" />
              </IconContainer>
              <Stack gap={0.5}>
                <Stack direction="row" alignItems="center" gap={0.5}>
                  <Typography noWrap variant="h5">
                    {row.name}
                  </Typography>
                  <Typography color="text.muted">{row.id}</Typography>
                </Stack>
                {isMultipleTeamsEnabled ? (
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <TeamName
                      name={row.team.name}
                      icon={row.team.icon}
                      avatarSx={{
                        width: 16,
                        height: 16,
                        fontSize: row.team.icon ? 12 : 10,
                        borderRadius: 1 / 2,
                      }}
                      gap={0.5}
                      nameSx={{ fontSize: 12, fontWeight: 500, color: 'text.subtle' }}
                    />
                    <Typography variant="body2" color="text.subtle">
                      •
                    </Typography>
                    <Typography variant="body2" color="text.subtle">
                      Last activity {displayTime}
                    </Typography>
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.subtle">
                    last activity {displayTime}
                  </Typography>
                )}
              </Stack>
            </Stack>
          );
        },
      },
      {
        field: 'status',
        headerName: 'Production',
        sortable: false,
        disableColumnMenu: true,
        minWidth: 200,
        renderCell: ({ row }) => {
          if (row.__typename === 'ModelType') {
            return <ProductionStatus primary={row.primaryVersion} />;
          }

          return <ProductionStatus primary={row.primaryDeployment} />;
        },
      },
      {
        field: 'versions',
        headerName: 'Deployments',
        sortable: false,
        disableColumnMenu: true,
        minWidth: 200,
        renderCell: ({ row }) => {
          return (
            <VersionsCountByStatus
              options={sortVersionsByStatus(
                getVersionsByStatus(
                  row.__typename === 'ModelType'
                    ? row.versionCountByStatus
                    : row.deploymentCountByStatus,
                  undefined,
                  true,
                ),
              )}
            />
          );
        },
      },
      {
        field: 'action',
        headerName: '',
        sortable: false,
        disableColumnMenu: true,
        minWidth: 110,
        renderCell: ({ row }) => {
          if (row.__typename === 'ModelType') {
            return (
              <Stack direction="row" gap={0.2}>
                {modelsPinnedAPI.getPinActionItem(row.id, row.name)}
                <ModelActions
                  modelId={row.id}
                  modelName={row.name}
                  variant="minimal"
                  doNotRedirect
                />
              </Stack>
            );
          }

          return (
            <Stack direction="row" gap={0.2}>
              {chainsPinnedAPI.getPinActionItem(row.id, row.name)}
              <ChainActions chainId={row.id} chainName={row.name} variant="minimal" doNotRedirect />
            </Stack>
          );
        },
      },
    ];

    return (
      <Stack gap={2} mt={-3}>
        <Stack mx={-4}>
          <DataGrid<WorkspaceModelFragment | WorkspaceChainFragment>
            rows={entitiesToShow}
            columns={columns}
            rowHeight={ROW_HEIGHT}
            getRowId={(row) => row.id}
            sx={dataGridStyles}
            disableAutoHeight
            {...dataGridProps}
            slots={{
              row: (props) => (
                <AppLink
                  page={
                    props.row.__typename === 'ModelType'
                      ? BasetenPageEnum.Model
                      : BasetenPageEnum.Chain
                  }
                  params={{
                    [props.row.__typename === 'ModelType' ? 'modelId' : 'chainId']: props.row.id,
                  }}
                  key={props.row}
                  underline="none"
                  sx={{ color: 'inherit', '&:hover': { textDecoration: 'none' } }}
                >
                  <GridRow {...props} />
                </AppLink>
              ),
            }}
          />
        </Stack>
      </Stack>
    );
  }

  return (
    <Section gap={2}>
      <Stack gap={2}>
        <Typography variant="h3">Dedicated deployments</Typography>
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          <Search query={filterQuery} setQuery={setFilterQuery} />
          <ListOptions<SortBy>
            title="Sort"
            options={Object.values(SortBy)}
            selectedOption={selectedSortBy}
            onOptionSelect={setSelectedSortBy}
            mapOptionToTitle={(option) => mapSortByToTitle[option]}
          />
          <ListOptions<TypeFilter>
            title="Type"
            options={Object.values(TypeFilter)}
            selectedOption={selectedTypeFilter}
            onOptionSelect={setSelectedTypeFilter}
            mapOptionToTitle={(option) => mapTypeFilterToTitle[option]}
          />
          <ListOptions<Filter>
            title="Status"
            options={Object.values(Filter)}
            selectedOption={selectedFilter}
            onOptionSelect={setSelectedFilter}
            mapOptionToTitle={(option) => mapFilterToTitle[option]}
          />
        </Stack>
      </Stack>
      {getContent()}
    </Section>
  );
}

export default DedicatedDeployments;
